import type { CustomDateRange, TimeRange } from './TimeRangeSelector';

type PresetRange = Exclude<TimeRange, 'custom'>;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Duration each preset range covers.
 *
 * Typed as an exhaustive Record so adding a preset to TimeRange fails the build
 * until the duration is listed here, instead of silently falling back to 24h.
 */
export const PRESET_RANGE_MS: Record<PresetRange, number> = {
  '30m': 30 * MINUTE_MS,
  '60m': HOUR_MS,
  '12h': 12 * HOUR_MS,
  '24h': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
  '6m': 180 * DAY_MS,
  '1y': 365 * DAY_MS,
};

export interface TimeBounds {
  from: number;
  to: number;
}

/**
 * The window the chart draws and labels: presets end at `now`, a custom range
 * uses its own bounds. Both the axis labels and the bar positions are derived
 * from this, so they cannot drift apart.
 */
export function resolveTimeBounds(
  selectedRange: TimeRange,
  customRange: CustomDateRange | undefined,
  now: number
): TimeBounds {
  if (selectedRange === 'custom') {
    if (customRange) {
      return { from: customRange.from.getTime(), to: customRange.to.getTime() };
    }
    return { from: now - DAY_MS, to: now };
  }

  return { from: now - PRESET_RANGE_MS[selectedRange], to: now };
}

/**
 * EDMS only accepts a lookback from now, so a custom range that ends in the
 * past still has to be fetched all the way back from `now`.
 */
export function edmsLookbackMs(bounds: TimeBounds, now: number): number {
  return Math.max(now - bounds.from, MINUTE_MS);
}

/** Format a duration as the `d.hh:mm:ss` timespan EDMS expects. */
export function toEdmsTimeRange(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${days}.${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Bucket count to request for a lookback window, keeping bucket size sensible per tier. */
export function computeNumDataPoints(ms: number): number {
  const hours = ms / HOUR_MS;
  if (hours <= 1) return 60; // ~1 min buckets
  if (hours <= 12) return 72; // ~10 min buckets
  if (hours <= 24) return 96; // ~15 min buckets
  if (hours <= 7 * 24) return 56; // ~3 hour buckets
  if (hours <= 30 * 24) return 60; // ~12 hour buckets
  if (hours <= 180 * 24) return 26; // ~weekly
  return 52; // ~weekly over a year
}
